import { reportCommerceApiErrorToSentry } from './error-reporting';

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8787/v1').replace(/\/+$/, '');
const COMMERCE_API_TIMEOUT_MS = 30000;
const COMMERCE_API_GET_MAX_ATTEMPTS = 2;
const RETRYABLE_STATUS_CODES = new Set([408, 429, 502, 503, 504]);

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getRetryDelayMs(attempt: number) {
  return attempt * 300;
}

function isAbortError(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

function normalizePath(path: string) {
  if (!path) {
    return '/';
  }

  return path.startsWith('/') ? path : `/${path}`;
}

function shouldRetryResponse(status: number) {
  return RETRYABLE_STATUS_CODES.has(status);
}

function getAttemptCount(method?: string) {
  return (method ?? 'GET').toUpperCase() === 'GET' ? COMMERCE_API_GET_MAX_ATTEMPTS : 1;
}

function createFetchTimeoutSignal(timeoutMs: number, externalSignal?: AbortSignal) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  const abortFromCaller = () => {
    controller.abort();
  };

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', abortFromCaller, { once: true });
    }
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      if (externalSignal) {
        externalSignal.removeEventListener('abort', abortFromCaller);
      }
    },
  };
}

export class CommerceApiError extends Error {
  status: number;
  payload: unknown;
  path: string;
  publicMessage: string;
  requestId: string | null;

  constructor({
    status,
    path,
    publicMessage,
    payload,
    requestId,
  }: {
    status: number;
    path: string;
    publicMessage: string;
    payload: unknown;
    requestId: string | null;
  }) {
    super(publicMessage);
    this.name = 'CommerceApiError';
    this.status = status;
    this.path = path;
    this.publicMessage = publicMessage;
    this.payload = payload;
    this.requestId = requestId;
  }
}

function getPayloadMessage(payload: unknown) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const candidate = payload as {
    detail?: unknown;
    title?: unknown;
    message?: unknown;
  };

  if (typeof candidate.detail === 'string' && candidate.detail.trim()) {
    return candidate.detail;
  }

  if (typeof candidate.title === 'string' && candidate.title.trim()) {
    return candidate.title;
  }

  if (typeof candidate.message === 'string' && candidate.message.trim()) {
    return candidate.message;
  }

  return null;
}

function getPublicMessageForStatus(status: number) {
  if (status === 0) {
    return 'We could not connect to the store right now. Please check your connection and try again.';
  }

  if (status === 400 || status === 422) {
    return 'Please check the information and try again.';
  }

  if (status === 401) {
    return 'Please sign in to continue.';
  }

  if (status === 403) {
    return 'You do not have access to this action.';
  }

  if (status === 404) {
    return 'We could not find that item right now.';
  }

  if (status === 409) {
    return 'That change could not be saved because the item has changed. Please refresh and try again.';
  }

  if (status === 429) {
    return 'Too many attempts. Please wait a moment and try again.';
  }

  return 'The store is temporarily unavailable. Please try again in a few moments.';
}

function getRequestId(response: Response, payload: unknown) {
  const headerRequestId = response.headers.get('x-request-id');
  if (headerRequestId) {
    return headerRequestId;
  }

  if (payload && typeof payload === 'object' && typeof (payload as { instance?: unknown }).instance === 'string') {
    const instance = (payload as { instance: string }).instance;
    const match = instance.match(/\/errors\/(.+)$/);
    return match?.[1] ?? null;
  }

  return null;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const normalizedPath = normalizePath(path);
  const headers = new Headers(init?.headers);

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  if (!headers.has('Content-Type') && init?.body !== undefined && init?.body !== null) {
    headers.set('Content-Type', 'application/json');
  }

  const attemptCount = getAttemptCount(init?.method);
  let response: Response | null = null;
  let networkError: unknown = null;

  for (let attempt = 1; attempt <= attemptCount; attempt += 1) {
    const { signal, cleanup } = createFetchTimeoutSignal(COMMERCE_API_TIMEOUT_MS, init?.signal ?? undefined);
    try {
      response = await fetch(`${API_BASE_URL}${normalizedPath}`, {
        credentials: 'include',
        ...init,
        headers,
        signal,
      });

      if (response.ok || response.status === 204) {
        break;
      }

      if (attempt < attemptCount && shouldRetryResponse(response.status)) {
        await wait(getRetryDelayMs(attempt));
        continue;
      }

      break;
    } catch (error) {
      networkError = error;

      const callerAborted = Boolean(init?.signal?.aborted);
      const shouldRetryNetworkError =
        attempt < attemptCount && !callerAborted && (isAbortError(error) || error instanceof TypeError);

      if (shouldRetryNetworkError) {
        await wait(getRetryDelayMs(attempt));
        continue;
      }

      break;
    } finally {
      cleanup();
    }
  }

  if (!response) {
    const apiError = new CommerceApiError({
      status: 0,
      path: normalizedPath,
      publicMessage: getPublicMessageForStatus(0),
      payload: null,
      requestId: null,
    });

    const internalMessage =
      isAbortError(networkError) && (!init?.signal || !init.signal.aborted)
        ? 'Request timed out'
        : networkError instanceof Error
          ? networkError.message
          : 'Network request failed';

    reportCommerceApiErrorToSentry({
      error: apiError,
      internalMessage,
    });
    throw apiError;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json().catch(() => null)) as {
    detail?: string;
    title?: string;
    message?: string;
  } | null;

  if (!response.ok) {
    const internalMessage = getPayloadMessage(payload);
    const apiError = new CommerceApiError({
      status: response.status,
      path: normalizedPath,
      publicMessage: getPublicMessageForStatus(response.status),
      payload,
      requestId: getRequestId(response, payload),
    });
    reportCommerceApiErrorToSentry({ error: apiError, internalMessage });
    throw apiError;
  }

  return payload as T;
}

function serializeJsonBody(path: string, body: unknown) {
  if (body === undefined) {
    return undefined;
  }

  if (typeof body === 'string') {
    const trimmed = body.trim();
    const looksLikeJsonObjectOrArray = trimmed.startsWith('{') || trimmed.startsWith('[');

    if (looksLikeJsonObjectOrArray) {
      try {
        JSON.parse(trimmed);
        return body;
      } catch (error) {
        const apiError = new CommerceApiError({
          status: 400,
          path: normalizePath(path),
          publicMessage: getPublicMessageForStatus(400),
          payload: null,
          requestId: null,
        });
        reportCommerceApiErrorToSentry({
          error: apiError,
          internalMessage:
            error instanceof Error
              ? `Malformed JSON string request body: ${error.message}`
              : 'Malformed JSON string request body.',
        });
        throw apiError;
      }
    }

    return JSON.stringify(body);
  }

  try {
    return JSON.stringify(body);
  } catch (error) {
    const apiError = new CommerceApiError({
      status: 400,
      path: normalizePath(path),
      publicMessage: getPublicMessageForStatus(400),
      payload: null,
      requestId: null,
    });
    reportCommerceApiErrorToSentry({
      error: apiError,
      internalMessage:
        error instanceof Error ? `JSON serialization failed: ${error.message}` : 'JSON serialization failed.',
    });
    throw apiError;
  }
}

export const commerceApi = {
  get: <T>(path: string) => request<T>(normalizePath(path)),
  post: async <T>(path: string, body?: unknown) => {
    const serializedBody = serializeJsonBody(path, body);

    return request<T>(path, {
      method: 'POST',
      body: serializedBody,
    });
  },
  put: async <T>(path: string, body?: unknown) => {
    const serializedBody = serializeJsonBody(path, body);

    return request<T>(path, {
      method: 'PUT',
      body: serializedBody,
    });
  },
  delete: <T>(path: string) =>
    request<T>(path, {
      method: 'DELETE',
    }),
};

export function buildQueryString(params: Record<string, string | number | boolean | null | undefined>) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

export function getCommerceErrorMessage(error: unknown, fallback: string) {
  if (error instanceof CommerceApiError) {
    return error.publicMessage;
  }

  return fallback;
}

export function isCommerceNotFoundError(error: unknown) {
  return error instanceof CommerceApiError && error.status === 404;
}
